<script setup lang="tsx">
import type { Vulnerability, SaveVulnerabilitiesRequest, VulnerabilityMeta, RiskVulnerability, Response } from '@/models/api-generated'
import http from '@/services/http'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import VulnerabilityAutocomplete from '../vulnerability/VulnerabilityAutocomplete.vue'
import IconButton from '@/components/shared/IconButton.vue'
import AddVulnerabilityModal from '../AddNewVulnerabilityModal.vue'
import { defaultInstance } from '@/services/utils'

const props = defineProps({
	riskMetaId: { type: Number, required: true },
	isEdit: { type: Boolean, required: true },
})
const model = defineModel<VulnerabilityMeta[]>({ required: true })

const { t } = useI18n()
const selectedVulnerability = ref<VulnerabilityMeta>()
const showNewVulnerabilityModal = ref(false)

watch(selectedVulnerability, async (val) => {
	if (val) {
		await addSelectedVulnerability(val)
		selectedVulnerability.value = undefined
	}
})

async function addSelectedVulnerability(vulnerability: VulnerabilityMeta) {
	if (!vulnerability || model.value.some((t) => t.id === vulnerability.id)) return
	model.value.push(vulnerability)

	const request: RiskVulnerability = {
		riskMetaId: props.riskMetaId,
		vulnerabilityMetaId: vulnerability.id,
	}
	await http.post('api/vulnerability/riskVulnerabilityConnections', request)
}

async function quickCreateVulnerability(payload: VulnerabilityMeta) {
	const vulnerability: Vulnerability = {
		name: payload.vulnerability.name,
		code: payload.vulnerability.code,
		description: payload.vulnerability.description,
		isCustom: true,
	} as Vulnerability

	const vulnerabilityMeta: VulnerabilityMeta = {
		vulnerability,
		instance: defaultInstance(),
		catalogMeta: payload.catalogMeta,
	} as VulnerabilityMeta

	const request: SaveVulnerabilitiesRequest = {
		changed: [],
		deleted: [],
		added: [vulnerabilityMeta],
	}

	const { data } = await http.post<Response<VulnerabilityMeta[]>>(`api/vulnerability/save`, request)
	if (!data.data) return
	const created = data.data[0]
	await addSelectedVulnerability(created)
}

async function removeVulnerability(meta: VulnerabilityMeta) {
	const request: RiskVulnerability = {
		riskMetaId: props.riskMetaId,
		vulnerabilityMetaId: meta.id,
	}
	await http.delete('api/vulnerability/riskVulnerabilityConnections', {
		data: request,
	})

	model.value = model.value.filter((m) => m.vulnerability.id !== meta.vulnerability.id)
}

async function saveNewVulnerability(payload: VulnerabilityMeta) {
	await quickCreateVulnerability(payload)
	showNewVulnerabilityModal.value = false
}
</script>

<template>
	<div class="card flex-fill">
		<div class="card-body">
			<h6 class="card-title text-center fw-bold">{{ t('risk.vulnerability', 2) }} ({{ model.length }})</h6>

			<ul class="list-unstyled mb-3">
				<li
					v-for="meta in model"
					:key="meta.vulnerability.id"
					:data-test="'vulnerability-item-' + meta.id"
					class="item-accent border rounded px-2 py-1 mb-1 d-flex justify-content-between align-items-center"
				>
					<span v-one-tooltip="meta.vulnerability.description"> {{ meta.vulnerability.name }} ({{ meta.vulnerability.code }}) </span>

					<IconButton
						v-if="props.isEdit"
						:data-test="'delete-vulnerability-risk' + meta.id"
						class="bi bi-trash text-danger"
						@click="removeVulnerability(meta)"
						:title="$t('misc.delete')"
						size="fs-5"
					/>
				</li>
			</ul>

			<template v-if="props.isEdit">
				<VulnerabilityAutocomplete
					v-model="selectedVulnerability"
					:exclude="model.map((m) => m.id)"
					data-test="vulnerability-autocomplete"
					:label="$t('misc.search')"
				/>

				<div class="d-flex justify-content-center mt-3">
					<button
						:data-test="'add-new-vulnerability-' + props.riskMetaId"
						type="button"
						class="btn btn-outline-primary btn-sm"
						@click="showNewVulnerabilityModal = true"
					>
						<i class="bi bi-plus-circle me-1"></i>
						{{ $t('vulnerability.addVulnerability') }}
					</button>
				</div>

				<AddVulnerabilityModal v-model="showNewVulnerabilityModal" :risk-meta-id="props.riskMetaId" @confirm="saveNewVulnerability" />
			</template>
		</div>
	</div>
</template>
